import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  getIsThirdPartyMember,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import _orderBy from 'lodash/orderBy';
import { Button, MiniLoader, PageTitle, Alert } from '@pwc/pwc-deals-ui';
import {
  CommentTarget,
  CommentType,
  Constants,
  inlineCommentLicalization,
  USER_ROLES,
  NavigationNodeId,
} from '@pwc/pwc-deals-web-common';

import './view-hypothesis.scss';

import useMobile from 'common/hooks/useMobile';
import featureFlags, {
  isInlineCommentingEnabled,
} from 'common/helpers/featureFlags';
import AutoLock from 'common/components/AutoLock';
import FieldCommentWrapper from 'common/components/FieldCommentWrapper';
import { LoadingError as LoadingErrorVersioning } from 'features/versioning/common/LoadingError';
import { SyncedInfoProvider } from 'common/components/Autosync/SyncedInfoProvider';
import AddInsightButton from 'common/components/AddInsightButton/AddInsightButton';
import { useKfaTitle } from '../../../common/hooks/useKfaTitle';

import { updateLibraryItem } from '../../analyses/libraryItemsSlice';
import useAllConnections from '../../narratives/hooks/useAllConnections';
import { useIsNonCommentableContent } from '../../../common/hooks/useIsNonCommentableContent';
import ViewHypothesisHeader from './ViewHypothesisHeader';
import updateEntityVersion from '../../../common/actions/updateEntityVersion';
import {
  ENTITY_TYPES,
  USER_TYPE_CLASSES,
} from '../../../common/constants/General';
import CalloutBox from '../../../common/components/CalloutBox/CalloutBox';
import { FormLayout } from '../../../common/components/layouts';
import { LinkedContentNavigation } from '../../../common/components/LinkedContentNavigation/LinkedContentNavigation';
import ModalConfirmation from '../../../common/components/ModalConfirmation';
import PageContentLabel from '../../../common/components/PageContentLabel/PageContentLabel';
import Section from '../../../common/components/Section/Section/Section';
import { getViewClientInfo } from '../../../common/helpers/viewClientVersion';
import CalloutBoxCopy from '../../../common/components/CalloutBox/CalloutBoxCopy';
import {
  useApprovalWorkflow,
  WORKFLOW_ACTIONS,
} from '../../../common/hooks/useApprovalWorkflow';
import useVersionToggle from '../../../common/hooks/useVersionToggle';
import useIsMountedRef from '../../../common/hooks/useIsMountedRef';
import CommentEditor from '../../../common/plugins/CommentEditor';
import { showMoreMenuError } from '../../../common/redux/errors/errorsSlice';
import userTokenService from '../../../common/services/security/userTokenService';
import {
  editHypothesisPath,
  hypothesesListPath,
  reportPreviewPath,
} from '../../../common/services/urlService';
import TagList from '../../analyses/common/TagList/TagList';
import { entitySharedStatusUpdateSuccess } from '../../comments/commentsSlice';
import NewConnectedContent from '../../narratives/ConnectedContent/NewConnectedContent';
import useConnectedContent from '../../narratives/hooks/useConnectedContent';
import EnableForProjectState from '../../security/EnableForProjectState';
import ShowForRole from '../../security/ShowForRole';
import { getWorkflowStatus } from '../../workflow/workflowService';
import { deleteHypothesis } from '../hypothesesService';
import {
  updateContentWorklow,
  shareContent,
} from '../../../common/services/contentWorkflowService';
import { getPowerBiReportData } from '../../analyses/powerBiService';
import {
  getHypothesisLoading,
  getHypothesisErrorState,
  loadHypothesisVersions,
  loadKfa,
  resetHypothesis,
  selectClientVersion,
  selectDraftVersion,
  selectHypothesisVersions,
  selectKfa,
  updateVersions,
  loadKfaVersions,
  loadKfaSharedPrevious,
  updateSelectionVersion,
  selectedVersionStatus,
  fetchHypothesisBegan,
} from '../hypothesisSlice';
import { loadLibraryItems, prepareLibraryItems } from '../libraryItemsSupports';
import Thumbnail from '../Thumbnail/Thumbnail';
import StatusViewer from '../../../common/components/StatusViewer/StatusViewer';
import useTabletL from '../../../common/hooks/useTabletL';
import LoadingError from '../../../common/components/LoadingError';
import ResponsiveLoader from '../../../common/components/ResponsiveLoader/ResponsiveLoader';
import { useIsAddingCommentEnabled } from '../../../common/hooks/useIsAddingCommentEnabled';
import RichTextReadOnlyComment from '../../../common/components/RichTextForm/RichTextReadOnlyComment';
import { useShowInlineComments } from '../../../common/hooks/useShowInlineComments';
import { getProject } from '../../project/projectSlice';

const ViewHypothesis = () => {
  const { setDefaultCloseCommentPanel } = useShowInlineComments();
  const kfaTitle = useKfaTitle();
  const isMountedRef = useIsMountedRef();
  const { projectId, hypothesisId: hypothesisContentThreadId } = useParams();
  const dispatch = useDispatch();

  const userRole = useSelector(getUserRole);
  const isPwcUser = userRole === USER_ROLES.PWC_USER;
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);
  const selectedVersions = useSelector(selectHypothesisVersions);
  const selectedVersion = useSelector(selectedVersionStatus);
  const draftVersion = useSelector(selectDraftVersion);
  const clientVersion = useSelector(selectClientVersion);
  const { t } = useTranslation();
  /**
   * ! jm - Due to Global Toggle which works with user roles, we need to reset
   * ! the KFA in redux state on the first load of this component, rather
   * ! than doing it during unmount. In the future, we should avoid storing
   * ! the KFA data in redux altogether. Only then we can remove this.
   */
  useEffect(() => {
    return () => {
      dispatch(resetHypothesis());
    };
  }, [dispatch]);

  useEffect(() => {
    if (featureFlags.isVersioningPhase1Enabled) {
      // handle redirect from edit screen, edit screen should already populate versions, so do not touch
      if (!selectedVersions || !selectedVersions.length) {
        dispatch(
          loadKfaVersions(
            projectId,
            hypothesisContentThreadId,
            !isPwcUser,
            isMountedRef
          )
        );
      }
    } else {
      dispatch(
        loadKfaVersions(
          projectId,
          hypothesisContentThreadId,
          !isPwcUser,
          isMountedRef
        )
      );
    }
    // do not add selectedVersions, it's going to freeze loop, we only want to check it once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, projectId, hypothesisContentThreadId, isPwcUser, isMountedRef]);

  const isMobile = useMobile();
  const history = useHistory();
  const isTabletL = useTabletL();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const isLoading = useSelector(getHypothesisLoading);

  const statusError = useSelector(getHypothesisErrorState);
  const { navigation } = useSelector(getProject);
  const onLoadingPreviousVersionErrorRetryFN = useRef(undefined);
  const onLoadingPreviousVersionError = useCallback(() => {
    if (statusError.hasError) {
      return onLoadingPreviousVersionErrorRetryFN?.current();
    }

    return undefined;
  }, [statusError.hasError]);
  const kfa = useSelector(selectKfa);
  const kfaContent = kfa && kfa.content;
  const [enhancedSections, setEnhancedSections] = useState([]);
  const [thumbnail, setThumbnail] = useState();
  const [loadingItems, setLoadingItems] = useState(true);
  const [transformError, setTransformError] = useState(false);

  const userTypeClass = USER_TYPE_CLASSES[userRole] || '';

  const isNonCommentableContent = useIsNonCommentableContent();
  const isAddingCommentEnabled = useIsAddingCommentEnabled();
  const isReadOnly = userTokenService.isPwcReadOnlyRole(projectId);
  const showNonCommentableContent =
    isInlineCommentingEnabled && isNonCommentableContent && !isReadOnly;

  const { isEntitySharedWithClient, commentCounts } = useVersionToggle(
    isMountedRef.current,
    draftVersion,
    clientVersion
  );
  const linkedConnections = useConnectedContent(
    kfaContent && kfaContent.contentId
  );
  const { allConnections: parentAllRetrievedConnections, error: parentError } =
    useAllConnections(isMountedRef.current);
  const { versionStatus = 0 } = kfaContent || {};

  const contextStatusInfoItem = useMemo(
    () => ({
      shareWithAll: isEntitySharedWithClient,
      versionStatus: kfa?.content.versionStatus,
      userViewStatus: kfa?.content.userViewStatus,
      isEditMode: false,
      isViewMode: true,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kfa]
  );

  const hasSharedSelected =
    selectedVersions?.some(({ isSelected }) => isSelected) || false;

  const hasSharedSelectedPrevious =
    selectedVersions?.some(
      ({ isSelected, isPreviousVersion }) => isSelected && isPreviousVersion
    ) || false;

  const hasLatestButNotDraftVersion =
    selectedVersions?.some(
      ({ isSelected, isPreviousVersion }) => isSelected && !isPreviousVersion
    ) || false;

  const isSelectedVersionADraft = useMemo(() => {
    if (selectedVersion && selectedVersions) {
      return selectedVersions.some(
        (x) =>
          x.contentId === selectedVersion.contentId &&
          x.versionStatus === Constants.VERSION_STATUS.DRAFT.value
      );
    }

    return false;
  }, [selectedVersion, selectedVersions]);

  /**
   * Only show for draft, hide the rest
   */
  const hideTextModifiedBy = useMemo(() => {
    if (draftVersion) {
      if (!isSelectedVersionADraft) {
        return true;
      }

      return false;
    }

    return true;
  }, [draftVersion, isSelectedVersionADraft]);

  const statusContent = useMemo(() => {
    return featureFlags.isVersioningPhase1Enabled
      ? {
          ...kfaContent,
          modifiedBy: hideTextModifiedBy ? undefined : kfaContent?.modifiedBy,
        }
      : kfaContent;
  }, [hideTextModifiedBy, kfaContent]);

  const dispatchUpdateSelectionVersion = useCallback(
    (selectedVersionInput) => {
      if (
        selectedVersion?.contentId !== selectedVersionInput.contentId ||
        !selectedVersion
      ) {
        dispatch(updateSelectionVersion(selectedVersionInput));
      }
    },
    [dispatch, selectedVersion]
  );

  useEffect(() => {
    setLoadingItems(true);
    if (!kfa) {
      return;
    }
    function dispatchExcelLibraryItems() {
      return (libItems) => {
        for (let i = 0; i < libItems?.length; i += 1) {
          const libItem = libItems[i];
          if (
            [
              Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value,
              Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value,
            ].includes(libItem.type)
          ) {
            dispatch(
              updateLibraryItem({
                id: libItem.id,
                item:
                  libItem.type === 300 || libItem.type === 500
                    ? libItem.value
                    : libItem.value,
                libraryItemFormat: libItems.filter(
                  (ls) => ls.id === libItem.id
                ),
              })
            );
          }
        }
      };
    }
    const {
      libraryItemGroups,
      sharedLibraryItems,
      items,
      content: { sections, insightWithItems },
    } = kfa;
    (async () => {
      let preparedItems = null;
      try {
        preparedItems = await prepareLibraryItems({
          projectId,
          libraryItemGroups,
          sections,
          sharedLibraryItems: sharedLibraryItems || items,
          contextStatusInfoItem,
          excelLibraryItemsCallback: dispatchExcelLibraryItems,
          isPwcUser,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        setTransformError(true);
      }

      try {
        const itemsTemp = await loadLibraryItems({
          projectId,
          libraryItemGroups,
          sections,
          sharedLibraryItems: sharedLibraryItems || items,
          contextStatusInfoItem,
          excelLibraryItemsCallback: dispatchExcelLibraryItems,
          ...preparedItems,
          caller: 'ViewHypothesis1',
        });
        setEnhancedSections(itemsTemp);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        setTransformError(true);
      }

      try {
        const newInsight = await loadLibraryItems({
          projectId,
          libraryItemGroups,
          sections: [insightWithItems],
          sharedLibraryItems: sharedLibraryItems || items,
          contextStatusInfoItem,
          ...preparedItems,
          caller: 'ViewHypothesis2',
        });
        if (newInsight[0]) {
          setThumbnail(newInsight[0].items[0]);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        setTransformError(true);
      }
      setLoadingItems(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kfa, projectId, isPwcUser]);

  const sortedSectionByOrder = _orderBy(enhancedSections, [
    (section) => section.order,
  ]);

  useEffect(() => {
    const powerBiItems = kfa?.libraryItemGroups?.powerBiLibraryItemList;
    if (powerBiItems) {
      powerBiItems.forEach((powerBiItem) => {
        powerBiItem.powerBiLibraryItems.forEach((powerBiLibraryItem) => {
          const payload = {
            data: powerBiLibraryItem.data,
            name: powerBiLibraryItem.name,
            workspaceId: powerBiLibraryItem.workspaceId,
            libraryItemType: powerBiLibraryItem.type,
            libraryItemId: powerBiLibraryItem.id,
            embedUrl: powerBiItem.embedUrl,
            reportId: powerBiItem.reportId,
          };
          return getPowerBiReportData(projectId, payload);
        });
      });
    }
  }, [kfa, projectId]);

  const handleDeleteHypothesis = async () => {
    setShowDeleteConfirmation(false);
    const success = await deleteHypothesis(
      projectId,
      hypothesisContentThreadId
    );
    if (success) {
      history.replace(hypothesesListPath(projectId));
    }
  };

  const getDraftContent = useCallback(() => {
    const { contentId } = draftVersion;
    dispatch(entitySharedStatusUpdateSuccess(false));
    dispatch(loadKfa(projectId, contentId, false, isMountedRef));
    dispatchUpdateSelectionVersion({ contentId });
  }, [
    dispatch,
    dispatchUpdateSelectionVersion,
    draftVersion,
    isMountedRef,
    projectId,
  ]);

  const getSharedContent = useCallback(() => {
    const { contentId } = clientVersion;

    dispatch(entitySharedStatusUpdateSuccess(true));

    dispatch(loadKfa(projectId, contentId, true, isMountedRef));
    dispatchUpdateSelectionVersion({ contentId });
  }, [
    clientVersion,
    dispatch,
    dispatchUpdateSelectionVersion,
    isMountedRef,
    projectId,
  ]);

  const getSharedContentPreviousOrCurrentSelected = useCallback(
    ({ contentId }) => {
      dispatch(entitySharedStatusUpdateSuccess(true));
      dispatch(
        loadKfaSharedPrevious({
          contentID: contentId,
          projectID: projectId,
          isMountedRef,
        })
      );
      dispatchUpdateSelectionVersion({ contentId });
    },
    [dispatch, dispatchUpdateSelectionVersion, isMountedRef, projectId]
  );

  useEffect(() => {
    if (selectedVersions) {
      if (featureFlags.isVersioningPhase1Enabled) {
        const sharedVersion = clientVersion;

        const versionPreviousSelected = selectedVersions.find(
          ({ isPreviousVersion, isSelected }) => isPreviousVersion && isSelected
        );

        if (versionPreviousSelected) {
          onLoadingPreviousVersionErrorRetryFN.current = () =>
            getSharedContentPreviousOrCurrentSelected({
              contentId: versionPreviousSelected.contentId,
            });
          onLoadingPreviousVersionErrorRetryFN.current();
          dispatch(updateEntityVersion(versionPreviousSelected.versionNumber));
        } else if (
          draftVersion &&
          !isEmpty(draftVersion) &&
          !sharedVersion?.isSelected &&
          !isThirdPartyMember
        ) {
          getDraftContent();
          dispatch(updateEntityVersion(draftVersion.versionNumber));
        } else if (sharedVersion && !isEmpty(sharedVersion)) {
          getSharedContent();
          dispatch(updateEntityVersion(sharedVersion.versionNumber));
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (draftVersion && !isEmpty(draftVersion)) {
          getDraftContent();
          dispatch(updateEntityVersion(draftVersion.versionNumber));
        } else if (clientVersion && !isEmpty(clientVersion)) {
          getSharedContent();
          dispatch(updateEntityVersion(clientVersion.versionNumber));
        }
      }
    }
    /**
     * Don't add the callbacks which depends on selectedVersion which is updated all the time
     * getDraftContent, getSharedContent, getSharedContentPreviousOrCurrentSelected
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientVersion, dispatch, draftVersion, projectId, selectedVersions]);

  const gotoEditHypothesis = () =>
    history.push(editHypothesisPath(projectId, hypothesisContentThreadId));

  const workflowActions = useApprovalWorkflow(
    getWorkflowStatus,
    async (_projectId, contentId, step) => {
      try {
        if (step === WORKFLOW_ACTIONS.SHARE) {
          dispatch(fetchHypothesisBegan());
          await shareContent(_projectId, `${contentId}`);
        } else {
          await updateContentWorklow(_projectId, `${contentId}`, step);
        }
      } catch (e) {
        dispatch(
          showMoreMenuError({
            itemId: contentId,
            moreMenuErrorType:
              Constants.MoreMenuErrorType.UNABLE_TO_SAVE_CHANGE,
          })
        );
        return;
      }

      dispatch(loadHypothesisVersions(projectId, hypothesisContentThreadId));
    },
    projectId,
    draftVersion && draftVersion.contentId,
    kfa,
    selectedVersions
  );

  const showClientVersion = useCallback(() => {
    if (!isLoading && clientVersion) {
      if (featureFlags.isVersioningPhase1Enabled) {
        const sharedVersion = clientVersion;

        if (selectedVersions?.length) {
          dispatch(
            updateVersions(
              /**
               * this component starts with or without versioning MFE, the versions in the store could be completely outdated or not there
               * so what happens here is we modify the versions so useEffect matches the logic to load previous version
               */
              selectedVersions.reduce((acc, item) => {
                const x = { ...item };
                if (
                  x.versionStatus === Constants.VERSION_STATUS.SHARED.value ||
                  x.versionStatus ===
                    Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
                ) {
                  Object.assign(x, {
                    isPreviousVersion: false,
                    isSelected: true,
                  });
                }
                acc.push(x);
                return acc;
              }, [])
            )
          );
        }

        dispatchUpdateSelectionVersion({
          contentId:
            selectedVersions?.find(
              (x) =>
                x.versionStatus === Constants.VERSION_STATUS.SHARED.value ||
                x.versionStatus ===
                  Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
            ).contentId || sharedVersion.contentId,
        });
      } else {
        getSharedContent();
        dispatch(updateEntityVersion(clientVersion.versionNumber));
      }
    }
    setDefaultCloseCommentPanel();
  }, [
    clientVersion,
    dispatch,
    dispatchUpdateSelectionVersion,
    getSharedContent,
    isLoading,
    selectedVersions,
    setDefaultCloseCommentPanel,
  ]);

  const showDraftVersion = useCallback(() => {
    if (!isLoading && draftVersion) {
      if (featureFlags.isVersioningPhase1Enabled) {
        if (selectedVersions?.length) {
          dispatch(
            updateVersions(
              // needed because useEffect will only match draft when shared is not selected
              selectedVersions.reduce((acc, item) => {
                const x = { ...item };
                if (
                  x.versionStatus === Constants.VERSION_STATUS.SHARED.value ||
                  x.versionStatus ===
                    Constants.VERSION_STATUS.SHARED_WITH_THIRD_PARTY.value
                ) {
                  x.isSelected = false;
                }
                acc.push(x);
                return acc;
              }, [])
            )
          );
        }
        dispatchUpdateSelectionVersion({ contentId: draftVersion.contentId });
      } else {
        getDraftContent();
        dispatch(updateEntityVersion(draftVersion.versionNumber));
      }
      setDefaultCloseCommentPanel();
    }
  }, [
    dispatch,
    dispatchUpdateSelectionVersion,
    draftVersion,
    getDraftContent,
    isLoading,
    selectedVersions,
    setDefaultCloseCommentPanel,
  ]);

  const viewClient = getViewClientInfo(
    showClientVersion,
    showDraftVersion,
    selectedVersions,
    kfaContent && kfaContent.versionNumber,
    kfaContent && kfaContent.versionStatus
  );

  const isClientView =
    !isPwcUser ||
    viewClient.isOnClientVersion ||
    versionStatus === Constants.VERSION_STATUS.SHARED.value;

  const scrollToBottom = () => {
    window.scroll({
      top: document.body.offsetHeight,
      left: 0,
      behavior: 'smooth',
    });
  };

  const isAnalysisVisible = () => {
    const item = navigation?.find(
      (nav) => nav.id === NavigationNodeId.ANALYSES
    );

    return item ? !item.isHidden : true;
  };

  const isLinkedNavVisible =
    isAnalysisVisible() &&
    (userTypeClass === 'client-user' ||
      isThirdPartyMember ||
      (isPwcUser && !isTabletL));

  const reportPreviewUrl = reportPreviewPath(
    projectId,
    hypothesisContentThreadId,
    viewClient.isOnClientVersion ? 'shared' : 'latest',
    false,
    ENTITY_TYPES.KFA
  );

  const isContentReady = !!(
    projectId &&
    !isLoading &&
    kfaContent &&
    kfaContent.contentThreadId
  );
  const commentingAutoLockContentId =
    isClientView && kfaContent
      ? [`content-${kfaContent.contentId}`]
      : [hypothesisContentThreadId];

  const headerComponent = (
    <>
      {kfaContent && (
        <ViewHypothesisHeader
          config={{
            commentCounts,
            draftVersion,
            sharedVersion: clientVersion,
            draftOnClick: showDraftVersion,
            sharedOnClick: showClientVersion,
          }}
          disableEdit={hasSharedSelected}
          disableMoreMenu={
            hasLatestButNotDraftVersion ? false : hasSharedSelected
          }
          disableVersionToggleButtons={
            hasLatestButNotDraftVersion ? false : hasSharedSelected
          }
          gotoEditHypothesis={gotoEditHypothesis}
          hideSelectedVersionToggleButtons={hasSharedSelectedPrevious}
          isEntitySharedWithClient={isEntitySharedWithClient}
          kfa={kfa}
          kfaContent={kfaContent}
          projectId={projectId}
          reportPreviewUrl={reportPreviewUrl}
          setShowDeleteConfirmation={setShowDeleteConfirmation}
          viewClient={viewClient}
          workflowActions={workflowActions}
          commentingAutoLockContentId={commentingAutoLockContentId[0]}
        />
      )}
      <ModalConfirmation
        show={showDeleteConfirmation}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteHypothesis}
        confirmTitle={t('areyouSureDeleteMsg')}
        confirmationMessage={t('contentPermanentlyDeleted', {
          contentName: kfaTitle,
        })}
        confirmButtonText={t('yesDelete')}
        dialogClassName="modal-confirmation"
        contentClassName="modal-confirmation"
      />
    </>
  );

  if (statusError.hasError || transformError) {
    switch (statusError.errorType) {
      case 'previousVersion':
        return (
          <>
            {isMobile && (
              <ViewHypothesisHeader
                disableEdit={hasSharedSelected}
                disableMoreMenu={
                  hasLatestButNotDraftVersion ? false : hasSharedSelected
                }
                disableVersionToggleButtons={
                  hasLatestButNotDraftVersion ? false : hasSharedSelected
                }
                hideEdit
                hideSelectedVersionToggleButtons={hasSharedSelectedPrevious}
                hideToggleVersion
                reportPreviewUrl={reportPreviewUrl}
                rightAlignItemsToright
                showWithoutContent
                setShowDeleteConfirmation={setShowDeleteConfirmation}
                viewClient={viewClient}
                workflowActions={workflowActions}
                commentingAutoLockContentId={commentingAutoLockContentId[0]}
              />
            )}

            <LoadingErrorVersioning
              contentMainType="unableToLoadPreviousVersion"
              propsActionButton={{
                children: 'Retry',
                onClick: onLoadingPreviousVersionError,
              }}
              propsTitle1={{
                children: 'Unable to load this version',
              }}
            />
          </>
        );

      default:
        return <LoadingError />;
    }
  }

  if (isLoading) {
    return <ResponsiveLoader />;
  }

  return isContentReady ? (
    <>
      <SyncedInfoProvider value={contextStatusInfoItem}>
        <AutoLock
          projectId={projectId}
          contentThreadIds={commentingAutoLockContentId}
        >
          <EnableForProjectState projectId={projectId}>
            {({ isProjectDisabled }) => (
              <FormLayout
                headerComponent={headerComponent}
                className="view-hypothesis-scroll-component"
              >
                <div
                  className="hypothesis-main-content"
                  data-testid="hypothesis-detail-page"
                >
                  {showNonCommentableContent && (
                    <Alert
                      className="hypothesis-non-commentable-content-banner"
                      type="info"
                      content={inlineCommentLicalization.nonCommentable(
                        isPwcUser
                      )}
                    />
                  )}
                  <div
                    className="hypothesis-card__contents"
                    data-testid="hypothesis-card-contents"
                  >
                    <div
                      className="hypothesis-card__contents__details"
                      data-testid="kfa-card-details"
                    >
                      <div
                        className="hypothesis-card__contents__details__title"
                        data-testid="kfa-card-title"
                      >
                        <div className="hypothesis-main-content__section">
                          <PageContentLabel
                            cssLabel="key-focus-area"
                            label={kfaTitle}
                          />
                        </div>
                        <StatusViewer
                          projectId={projectId}
                          content={statusContent}
                          allowDraftPill={!viewClient.isOnClientVersion}
                          shareWithAll={kfaContent?.shareWithAll}
                        />
                        <div className="hypothesis-main-content__section hypothesis-main-content__section--title">
                          <FieldCommentWrapper fieldKey={CommentTarget.Title}>
                            <PageTitle
                              title={kfaContent.title}
                              commentField={CommentTarget.Title}
                            />
                          </FieldCommentWrapper>
                        </div>
                      </div>
                      {!isPwcUser && (
                        <div className="check-linked-connections">
                          <Button
                            data-testid="popover-confirmation-cancel"
                            onClick={scrollToBottom}
                            className="button-link mr-3"
                          >
                            Check connections
                          </Button>
                        </div>
                      )}
                      <ShowForRole
                        projectId={projectId}
                        roles={Constants.USER_ROLE_GROUPS.CLIENT}
                      >
                        {kfaContent.insight && (
                          <div className="hypothesis-main-content__section">
                            <CalloutBox
                              iconName="light-bulb"
                              iconClass="icon fill icon-grey-theme"
                              headerLabel={t('insight')}
                              text={kfaContent.insight}
                              dataTestId="insight"
                              usage="detail"
                              enableInlineComment={isAddingCommentEnabled}
                              commentTarget={CommentTarget.Insight}
                            />
                          </div>
                        )}
                      </ShowForRole>

                      <ShowForRole
                        projectId={projectId}
                        roles={Constants.USER_ROLE_GROUPS.INTERNAL}
                      >
                        {kfaContent.insight && (
                          <div className="hypothesis-main-content__section">
                            <CalloutBox
                              iconName="light-bulb"
                              iconClass="icon fill icon-grey-theme"
                              headerLabel={t('insight')}
                              text={kfaContent.insight}
                              dataTestId="insight"
                              usage="detail"
                              enableInlineComment={isAddingCommentEnabled}
                              commentTarget={CommentTarget.Insight}
                            />
                          </div>
                        )}
                      </ShowForRole>

                      {!kfaContent.insight && (
                        <AddInsightButton
                          projectId={projectId}
                          contentThreadId={commentingAutoLockContentId[0]}
                          onClick={gotoEditHypothesis}
                        />
                      )}

                      <Thumbnail
                        projectId={projectId}
                        thumbnail={thumbnail}
                        editable={false}
                      />
                    </div>

                    <div className="hypothesis-card__contents__analyses-list">
                      {isLinkedNavVisible && (
                        <LinkedContentNavigation
                          connectedAnalyses={
                            linkedConnections?.connectedAnalyses
                          }
                          currentContent={kfaContent}
                          disableAllLinks={hasSharedSelectedPrevious}
                          hypothesisIndex={1}
                          isKfa
                          parentAllRetrievedConnections={
                            parentAllRetrievedConnections
                          }
                          parentContentId={kfaContent.contentId}
                          projectId={projectId}
                          sortByDateModified={false}
                          useParentConnections
                          parentError={parentError}
                          isCommentingAllowedOnThisItem
                        />
                      )}
                    </div>
                  </div>

                  {!isAddingCommentEnabled && (
                    <CalloutBoxCopy
                      fullCopy={kfaContent.body}
                      customClass="hypothesis-main-content__section__discription"
                    />
                  )}
                  {isAddingCommentEnabled && (
                    <RichTextReadOnlyComment
                      richText={kfaContent.body || ''}
                      commentType={CommentType.RichText}
                      commentTarget={CommentTarget.Description}
                      libraryItemId={CommentTarget.Description}
                    />
                  )}

                  {loadingItems && !sortedSectionByOrder.length && (
                    <div className="kfa-loader-wrap">
                      <MiniLoader />
                    </div>
                  )}
                  <div className="kfa-view-section-wrap">
                    {sortedSectionByOrder.map((section) => (
                      <Section
                        key={section.order}
                        projectId={projectId}
                        analysisId={
                          kfa && kfa.content && kfa.content.contentThreadId
                        }
                        section={section}
                        isLoading={loadingItems}
                        hasError={statusError.hasError}
                        commentingAutoLockContentId={
                          commentingAutoLockContentId
                        }
                      />
                    ))}
                  </div>
                  <ShowForRole
                    projectId={projectId}
                    roles={Constants.USER_ROLE_GROUPS.CLIENT}
                  >
                    <CommentEditor
                      entityType="hypothesis"
                      entityId={kfaContent.contentThreadId}
                      entityVersion={kfaContent.versionNumber}
                      isEntitySharedWithClient={kfaContent.shareWithAll}
                      isProjectDisabled={isProjectDisabled}
                    />
                  </ShowForRole>
                </div>
                <div className="kfa-view-connected-content-wrap">
                  {kfaContent.tags && kfaContent.tags.length > 0 && (
                    <>
                      <div className="section-onecolumn2-kfa-view" />
                      <TagList
                        tags={kfaContent.tags}
                        testId="hypothesis-tag-list"
                        isCommentingAllowed
                      />
                    </>
                  )}
                  {!viewClient.isOnClientVersion && (
                    <NewConnectedContent
                      projectId={projectId}
                      contentId={kfaContent.contentId}
                      linkedConnections={linkedConnections}
                      viewAsClient={isClientView}
                      sortByDateModified={false}
                    />
                  )}
                  <div className="bottom-linked-navigation-hypothesis">
                    {isLinkedNavVisible && (
                      <LinkedContentNavigation
                        connectedAnalyses={linkedConnections?.connectedAnalyses}
                        currentContent={kfaContent}
                        disableAllLinks={hasSharedSelectedPrevious}
                        hypothesisIndex={1}
                        isCommentingAllowedOnThisItem
                        isKfa
                        parentContentId={kfaContent.contentId}
                        parentAllRetrievedConnections={
                          parentAllRetrievedConnections
                        }
                        parentError={parentError}
                        projectId={projectId}
                        sortByDateModified={false}
                        useParentConnections
                      />
                    )}
                  </div>
                </div>
              </FormLayout>
            )}
          </EnableForProjectState>
        </AutoLock>
      </SyncedInfoProvider>
    </>
  ) : null;
};

export default ViewHypothesis;
